import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { fetchItems } from '../../../support/queries';
import { CacheKey } from '../../../types/cache-keys';
import { DEFAULT_QUERY_OPTIONS } from '../../../support/constants';
import {
  useFetchAssessment,
  useFetchResponseSets,
  useFetchSections,
} from '../../../hooks/assessment-hooks';
import {
  AssessmentQuestion,
  AssessmentResponseSet,
  byOrder,
} from '../../../support/assessments';
import ResponseSetDetail from './ResponseSetDetail';

/**
 * Admin review of the submissions collected against one assessment.
 *
 * A submissions table (respondent, submitted-at, and the stored headline stage when present) that
 * drills into one response set at a time. It loads the assessment's sections and questions once —
 * the same per-section query the builder and fill-in surface use — so the drill-down can rebuild the
 * section-key map the scorer needs and show each answer's option labels.
 */

type Props = {
  assessmentId: number;
  onBack: () => void;
};

const AssessmentResponsesReview: React.FC<Props> = ({ assessmentId, onBack }) => {
  const { data: assessment, isLoading: loadingAssessment } = useFetchAssessment(assessmentId);
  const { data: sectionsRaw = [] } = useFetchSections(assessmentId);
  const sections = useMemo(() => byOrder(sectionsRaw), [sectionsRaw]);

  const { data: responseSetsRaw = [], isLoading: loadingSets } = useFetchResponseSets(assessmentId);

  const questionQueries = useQueries({
    queries: sections.map((section) => ({
      queryKey: [String(CacheKey.AssessmentQuestion), String(section.id)],
      queryFn: () => fetchItems('db/assessment-question', { section: section.id }),
      enabled: !!section.id,
      ...DEFAULT_QUERY_OPTIONS,
    })),
  });

  const allQuestions = useMemo(
    () =>
      sections.flatMap(
        (section, index) => (questionQueries[index]?.data as AssessmentQuestion[]) || []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, questionQueries.map((q) => q.dataUpdatedAt).join(',')]
  );

  const sectionKeyById = useMemo(() => {
    const map: Record<number, string> = {};
    sections.forEach((section) => {
      map[section.id] = section.key;
    });
    return map;
  }, [sections]);

  const responseSets = useMemo(
    () =>
      [...(responseSetsRaw as AssessmentResponseSet[])].sort((a, b) =>
        (b.submitted_at || b.created || '').localeCompare(a.submitted_at || a.created || '')
      ),
    [responseSetsRaw]
  );

  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const selectedSet = responseSets.find((s) => s.id === selectedSetId) || null;

  if (loadingAssessment) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assessment) {
    return (
      <Box p={2}>
        <Button startIcon={<ArrowBack />} onClick={onBack}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Assessment not found.</Typography>
      </Box>
    );
  }

  if (selectedSet) {
    return (
      <ResponseSetDetail
        assessment={assessment}
        allQuestions={allQuestions}
        sectionKeyById={sectionKeyById}
        responseSet={selectedSet}
        onBack={() => setSelectedSetId(null)}
      />
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        All Assessments
      </Button>

      <Box mb={2}>
        <Typography variant="h6">{assessment.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          Submissions collected against this assessment.
        </Typography>
      </Box>

      <Paper variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Respondent</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell width={200}>Result</TableCell>
                <TableCell width={200}>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingSets ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={22} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : responseSets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No submissions yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                responseSets.map((set) => {
                  const summary = set.metadata?.summary as
                    | { stage?: number; stageName?: string }
                    | undefined;
                  return (
                    <TableRow key={set.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedSetId(set.id)}>
                      <TableCell>{set.respondent || 'Anonymous'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={set.status} />
                      </TableCell>
                      <TableCell>
                        {summary?.stage != null ? (
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`Stage ${summary.stage}${summary.stageName ? `: ${summary.stageName}` : ''}`}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {set.submitted_at ? new Date(set.submitted_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AssessmentResponsesReview;
